import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { getPredictionMarkets } from '../utils/api';
import { ethers } from 'ethers';

const LandingPage = () => {
  const navigate = useNavigate();
  const [featuredMarkets, setFeaturedMarkets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [metamaskLogin, setMetamaskLogin] = useState(false);
  const [metamaskAddress, setMetamaskAddress] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroFeatures = [
    {
      icon: 'TrendingUp',
      title: 'Predict the Future',
      description: 'Trade on the outcomes of real-world events with transparent, decentralized prediction markets.'
    },
    {
      icon: 'Shield',
      title: 'Secure & Transparent',
      description: 'All transactions are secured by blockchain technology with full transparency and auditability.'
    },
    {
      icon: 'Users',
      title: 'Community Driven',
      description: 'Join thousands of traders making predictions on politics, crypto, sports, and technology.'
    }
  ];

  const stats = [
    { label: 'Total Volume', value: '$2.4M', icon: 'DollarSign' },
    { label: 'Active Markets', value: '156', icon: 'TrendingUp' },
    { label: 'Traders', value: '12.5K', icon: 'Users' },
    { label: 'Resolved Markets', value: '1,249', icon: 'CheckCircle' }
  ];

  useEffect(() => {
    const fetchFeaturedMarkets = async () => {
      try {
        const response = await getPredictionMarkets('all', 6);
        if (response?.success) {
          setFeaturedMarkets(response?.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch featured markets:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedMarkets();

    checkMetamask();
  }, []);

  // Auto-slide for hero carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroFeatures?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [heroFeatures?.length]);

  const handleGetStarted = () => {
    navigate('/markets');
  };

  const handleViewMarket = (marketId) => {
    if (!metamaskLogin) return
    navigate(`/market/${marketId}`);
  };

  const formatVolume = (volume) => {
    if (volume >= 1000000) return `$${(volume / 1000000)?.toFixed(1)}M`;
    if (volume >= 1000) return `$${(volume / 1000)?.toFixed(0)}K`;
    return `$${volume}`;
  };

  const getOutcomeColor = (price) => {
    if (price > 0.7) return 'text-success';
    if (price > 0.5) return 'text-warning-400';
    return 'text-error';
  };

  const getDaysUntilEnd = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Ended';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  const connectMetamask = async () => {
    if (window.ethereum) {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      if (signer) {
        setMetamaskLogin(true);
        setMetamaskAddress(signer.address);
        localStorage.setItem('metamaskAddress', signer.address);
        window.location.reload();
      }
    }
  }

  const checkMetamask = async () => {
    setMetamaskLogin(false);
    const checkMetamaskAddress = localStorage.getItem('metamaskAddress');
    if (checkMetamaskAddress) {
      setMetamaskAddress(checkMetamaskAddress);
      setMetamaskLogin(true);
    }
  }

  const metamaskLogout = () => {
    localStorage.removeItem('metamaskAddress');
    setMetamaskAddress('');
    setMetamaskLogin(false)
    window.location.reload();
  }


  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/20 to-accent-900/20"></div>
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-7xl font-heading-bold text-text-primary mb-6 leading-tight">
              The Future is
              <span className="bg-gradient-primary bg-clip-text text-transparent"> Predictable</span>
            </h1>
            <p className="text-xl md:text-2xl text-text-secondary mb-8 max-w-3xl mx-auto leading-relaxed">
              Trade on the outcomes of real-world events. From politics to crypto, sports to technology - 
              predict what matters and earn from being right.
            </p>

            {metamaskLogin && metamaskAddress && (
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mt-4 mb-8">
                <button onClick={metamaskLogout}>
                  <Icon name="LogOut" className="w-6 h-6 mr-2" />
                </button>
                <span>{metamaskAddress}</span>
              </div>
            )}

            {metamaskLogin && (
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={handleGetStarted}
                  className="bg-gradient-primary text-white px-8 py-4 rounded-trading font-body-semibold text-lg hover:opacity-90 transition-opacity duration-trading-fast glow-primary min-w-[200px]"
                >
                  Start Trading
                </button>
                <button
                  onClick={() => navigate('/trading-dashboard')}
                  className="bg-surface-hover text-text-primary px-8 py-4 rounded-trading font-body-semibold text-lg hover:bg-border-hover border border-border transition-colors duration-trading-fast min-w-[200px]"
                >
                  View Dashboard
                </button>
              </div>
            )}

            {!metamaskLogin && (
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={connectMetamask}
                  className="bg-gradient-primary text-white px-8 py-4 rounded-trading font-body-semibold text-lg hover:opacity-90 transition-opacity duration-trading-fast glow-primary min-w-[200px]"
                >
                  Connect Metamask
                </button>
              </div>
            )}
          </div>

          {/* Hero Features Carousel */}
          <div className="relative max-w-4xl mx-auto">
            <div className="bg-surface border border-border rounded-trading shadow-trading-card p-8 backdrop-blur-sm">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center glow-primary">
                  <Icon name={heroFeatures?.[currentSlide]?.icon} size={32} color="white" />
                </div>
              </div>
              <h3 className="text-2xl font-heading-semibold text-text-primary text-center mb-4">
                {heroFeatures?.[currentSlide]?.title}
              </h3>
              <p className="text-lg text-text-secondary text-center leading-relaxed">
                {heroFeatures?.[currentSlide]?.description}
              </p>
              
              {/* Slide Indicators */}
              <div className="flex justify-center space-x-2 mt-6">
                {heroFeatures?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-trading-fast ${
                      index === currentSlide ? 'bg-primary glow-primary' : 'bg-border hover:bg-border-hover'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="py-16 px-6 bg-surface-hover">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats?.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 glow-primary">
                  <Icon name={stat?.icon} size={24} color="white" />
                </div>
                <div className="text-3xl font-data text-text-primary mb-2">{stat?.value}</div>
                <div className="text-sm text-text-secondary font-body-medium">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Featured Markets Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-heading-semibold text-text-primary mb-4">
              Featured Markets
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Explore the most popular prediction markets. Trade on outcomes that matter to you.
            </p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)]?.map((_, index) => (
                <div key={index} className="bg-surface border border-border rounded-trading p-6 animate-pulse">
                  <div className="h-4 bg-border rounded mb-4"></div>
                  <div className="h-3 bg-border rounded mb-2"></div>
                  <div className="h-3 bg-border rounded mb-4"></div>
                  <div className="flex space-x-4">
                    <div className="h-8 bg-border rounded flex-1"></div>
                    <div className="h-8 bg-border rounded flex-1"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {featuredMarkets?.map((market) => (
                <div
                  key={market?.id}
                  className="bg-surface border border-border rounded-trading shadow-trading-card hover:shadow-trading-modal transition-all duration-trading-normal cursor-pointer backdrop-blur-sm group"
                  onClick={() => handleViewMarket(market?.id)}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center glow-primary">
                          <Icon name="TrendingUp" size={16} color="white" />
                        </div>
                        <span className="text-xs bg-primary-900 text-primary-300 px-2 py-1 rounded">
                          {market?.category}
                        </span>
                      </div>
                      <span className="text-xs text-text-secondary flex items-center">
                        <Icon name="Clock" size={12} className="mr-1" />
                        {getDaysUntilEnd(market?.endDate)}
                      </span>
                    </div>

                    <h3 className="font-body-semibold text-text-primary mb-2 line-clamp-2 group-hover:text-primary transition-colors duration-trading-fast">
                      {market?.title}
                    </h3>
                    
                    <p className="text-sm text-text-secondary mb-4 line-clamp-2">
                      {market?.description}
                    </p>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {market?.outcomeTokens?.map((token, index) => (
                        <div key={index} className="bg-surface-hover rounded-trading p-3 text-center">
                          <p className="text-xs text-text-secondary mb-1">{token?.name}</p>
                          <p className={`font-data text-lg ${getOutcomeColor(token?.price)}`}>
                            ${token?.price?.toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-text-secondary">
                        Volume: {formatVolume(market?.totalVolume)}
                      </div>
                      {metamaskLogin && (
                        <div className="flex items-center text-primary text-sm font-body-medium group-hover:text-primary-300 transition-colors duration-trading-fast">
                          Trade Now
                          <Icon name="ArrowRight" size={16} className="ml-1" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {metamaskLogin && (
            <div className="text-center">
              <button
                onClick={() => navigate('/markets')}
                className="bg-gradient-primary text-white px-8 py-3 rounded-trading font-body-semibold hover:opacity-90 transition-opacity duration-trading-fast glow-primary"
              >
                View All Markets
              </button>
            </div>
          )}
        </div>
      </section>
      {/* How It Works Section */}
      <section className="py-20 px-6 bg-surface-hover">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-heading-semibold text-text-primary mb-4">
              How It Works
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Start predicting and earning in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                icon: 'Search',
                title: 'Choose a Market',
                description: 'Browse through hundreds of prediction markets on politics, crypto, sports, and more.'
              },
              {
                step: '02',
                icon: 'TrendingUp',
                title: 'Make Your Prediction',
                description: 'Buy shares in the outcome you believe will happen. Share prices reflect the probability.'
              },
              {
                step: '03',
                icon: 'Award',
                title: 'Earn Rewards',
                description: 'When the market resolves, winning shares are worth $1 each. Profit from being right!'
              }
            ]?.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-8">
                  <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center mx-auto glow-primary">
                    <Icon name={step?.icon} size={32} color="white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white font-data text-sm font-bold">
                    {step?.step}
                  </div>
                </div>
                <h3 className="text-xl font-heading-semibold text-text-primary mb-4">
                  {step?.title}
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  {step?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-primary-900/20 to-accent-900/20 rounded-trading p-12 backdrop-blur-sm">
            <h2 className="text-4xl font-heading-semibold text-text-primary mb-4">
              Ready to Start Trading?
            </h2>
            <p className="text-lg text-text-secondary mb-8">
              Join thousands of traders making predictions on the events that shape our world.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              {metamaskLogin && (
                <button
                  onClick={handleGetStarted}
                  className="bg-gradient-primary text-white px-8 py-4 rounded-trading font-body-semibold text-lg hover:opacity-90 transition-opacity duration-trading-fast glow-primary min-w-[200px]"
                >
                  Get Started Now
                </button>
              )}
              <button
                onClick={() => navigate('/login-register')}
                className="bg-surface text-text-primary px-8 py-4 rounded-trading font-body-semibold text-lg hover:bg-surface-hover border border-border transition-colors duration-trading-fast min-w-[200px]"
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;